<template>
    <div class="modal-container">
        <form-wrapper class="modal-form" :validator="$v.rateDetail">
            <header class="modal-header">
                <div class="modal-header__right">
                    <md-button class="md-simple md-just-icon md-round" @click.stop="$modal.hide">
                        <md-icon>clear</md-icon>
                    </md-button>
                </div>
            </header>

            <section class="modal-section">
                <div class="section-override">
                    <md-switch v-model="rateDetail.overrideRule">
                       Override
                    </md-switch>
                </div>
                <div class="section-heading">
                    <span class="section-heading__badge">Timeframe</span>
                    <p class="section-heading__copy">Choose the active window for this rule.</p>
                </div>
                <div class="section-card section-card--wide">
                    <rule-valid-days
                        :current-days="rateDetail.validForWeekDays"
                        :from-date="rateDetail.validFromDate"
                        :to-date="rateDetail.validToDate"
                        @daysSelected="handleDaysSeleceted"
                        ref="ruleValidDays"
                    />
                </div>
            </section>

            <section class="modal-section">
                <div class="section-heading">
                    <span class="section-heading__badge">Charging</span>
                    <p class="section-heading__copy">Set the base, interval and minimum thresholds.</p>
                </div>
                <div class="section-card section-card--compact">
                    <div class="charging-grid">
                        <div class="charging-group">
                            <h5 class="charging-group__title">Flag fall</h5>
                            <p class="charging-group__hint">Optional one-off amount before intervals apply.</p>
                            <form-group name="itemsFlagfall" label="Flag Fall">
                                <rule-operator
                                    v-if="$_nullChecker(rateDetail.itemsFlagfall)"
                                    v-model="rateDetail.itemsFlagfall.operator"
                                    :value="rateDetail.itemsFlagfall.operator"
                                />
                                <md-input v-model.number="rateDetail.itemsFlagfall.value" />
                            </form-group>
                        </div>
                        
                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Intervals</h5>
                            <p class="charging-group__hint">Breakpoints that trigger per item pricing.</p>
                            <div class="field-pair">
                                <form-group name="itemsInterval" label="Item Interval">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.itemsInterval)"
                                        v-model="rateDetail.itemsInterval.operator"
                                        :value="rateDetail.itemsInterval.operator"
                                    />
                                    <md-input v-model.number="rateDetail.itemsInterval.value" />
                                </form-group>
                                <form-group name="itemsRatePerInterval" label="Charge Per Interval">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.itemsRatePerInterval)"
                                        v-model="rateDetail.itemsRatePerInterval.operator"
                                        :value="rateDetail.itemsRatePerInterval.operator"
                                    />
                                    <md-input v-model.number="rateDetail.itemsRatePerInterval.value" />
                                </form-group>
                            </div>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Inclusions</h5>
                            <p class="charging-group__hint">Set how many items are included and any minimum charge.</p>
                            <div class="field-pair">
                                <form-group name="includedItems" label="Included Items">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.includedItems)"
                                        v-model="rateDetail.includedItems.operator"
                                        :value="rateDetail.includedItems.operator"
                                    />
                                    <md-input v-model.number="rateDetail.includedItems.value" />
                                </form-group>
                                <form-group name="itemsMinimumCharge" label="Minimum Charge">
                                    <rule-operator
                                        v-if="$_nullChecker(rateDetail.itemsMinimumCharge)"
                                        v-model="rateDetail.itemsMinimumCharge.operator"
                                        :value="rateDetail.itemsMinimumCharge.operator"
                                    />
                                    <md-input v-model.number="rateDetail.itemsMinimumCharge.value" />
                                </form-group>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <footer class="modal-footer">
                <div class="modal-footer__actions">
                    <md-button class="md-default dialog-button" @click.stop="$modal.hide">Cancel</md-button>
                    <md-button class="md-primary dialog-button" @click="create()">
                        {{ isUpdate ? 'Update' : 'Create' }}
                    </md-button>
                </div>
            </footer>
        </form-wrapper>
    </div>
</template>

<script>
import { decimal } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import { RATE_RULE } from '@/utils/defaults';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { handleRequests, showErrorMessage } from '@/helpers';
import { RATE_CHARGING_TYPES } from '@/utils/constants';
import { RuleValidDays } from '@/components';
import RuleOperator from './RuleOperator';

export default {
    name: 'CreateItemRuleContent',
    components: {
        RuleValidDays,
        RuleOperator
    },
    mixins: [GeneralMixin, RatesEngineMixin],
    props: {
        rateDetail: {
            type: Object,
            default: () => Object.assign({}, RATE_RULE()),
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        listLength: {
            type: Number,
            default: 0
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        customerId: {
            type: Number,
            default: null
        },
        rateGroupId: {
            type: Number,
            default: null
        },
        driverUserId: {
            type: Number,
            default: null
        },
        driverRuleCreation: {
            type: Boolean,
            default: false
        },
        teamRegionId: {
            type: Number,
            default: null
        }
    },
    created() {
        this.$_ensureChargeField('itemsRatePerInterval');
        this.$_ensureChargeField('itemsInterval');
        this.$_ensureChargeField('includedItems');
        this.$_ensureChargeField('itemsFlagfall');
        this.$_ensureChargeField('itemsMinimumCharge');
    },
    data() {
        return {
            ruleData: {
                type: null,
                rule: 'test rule data',
                customerId: null
            },
            rateChargingTypes: RATE_CHARGING_TYPES
        };
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    validations: {
        rateDetail: {
            itemsFlagfall: {
                value: {
                    decimal
                }
            },
            itemsRatePerInterval: {
                value: {
                    decimal
                }
            },
            itemsInterval: {
                value: {
                    decimal
                }
            },
            includedItems: {
                value: {
                    decimal
                }
            },
            itemsMinimumCharge: {
                value: {
                    decimal
                }
            },
        }
    },
    methods: {
        validateData() {
            if (this.$v.$invalid) {
                return false;
            }

            if (
                this.$v.rateDetail.itemsFlagfall.value.$model === null &&
                this.$v.rateDetail.itemsRatePerInterval.value.$model === null &&
                this.$v.rateDetail.itemsInterval.value.$model === null &&
                this.$v.rateDetail.includedItems.value.$model === null &&
                this.$v.rateDetail.itemsMinimumCharge.value.$model === null
            ) {
                return false;
            }

            return true;
        },
        async create() {
            const ruleDaysValid = this.$refs.ruleValidDays.validate();
            if (!ruleDaysValid) {
                return;
            }

            if (this.isUpdate) {
                this.updateRuleDetails();
                return;
            }

            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'PROCESSING...');
            const chargeType = this.rateChargingTypes.find((x) => x.key.toLowerCase() === this.$route.params.type);
            const obj = this.$_getRateRuleData(
                chargeType,
                this.user.publicUserId,
                this.rateDetail,
                this.customerId,
                this.listLength,
                this.rateGroupId,
                this.driverRuleCreation,
                this.driverUserId,
                this.teamRegionId
            );

            const endpoint = '/api/rates/create';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully created rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.data
                    });
                } else {
                    showErrorMessage(this, 'Failed creating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, error.data, null);
            }

            this.$_handleLoaderState(false);
        },
        async updateRuleDetails() {
            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'UPDATING...');

            const rateId = this.rateDetail.rateRulesId;
            this.rateDetail.condition = null;
            this.rateDetail.currency = null;
            this.rateDetail.priority = null;
            this.rateDetail.rateRulesId = null;

            const obj = {
                rateRulesId: rateId,
                rule: JSON.stringify(this.rateDetail)
            };

            const endpoint = '/api/rates/updateRate';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully updated rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.status
                    });
                } else {
                    showErrorMessage(this, 'Failed updating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, 'Failed updating rule.', null);
            }

            this.$_handleLoaderState(false);
        },
        handleDaysSeleceted(value) {
            this.rateDetail = Object.assign(this.rateDetail, value);
        }
    }
};
</script>

<style lang="scss" scoped>
@import './create-rule-content.shared.scss';
</style>
